import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Plane, ChevronDown, Crown, Briefcase } from "lucide-react";
import { NAV, NAV_B2B } from "@/data/nav";
import { NetworkLogo } from "@/components/NetworkLogo";
import { MainSponsor } from "@/components/MainSponsor";
import { useLang } from "@/i18n/LanguageContext";

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0 pr-1">
    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-coral">
      <Plane className="h-4 w-4 text-white" strokeWidth={2.5} />
    </span>
    <span className="font-display text-base font-extrabold tracking-tight text-ink leading-none">
      Anto<span className="text-coral">&</span>Bella
    </span>
  </Link>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { t, toggle } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const b2bActive = NAV_B2B.some((i) => i.to === pathname);

  return (
    <>
      {/* Barra flotante tipo píldora */}
      <header className="fixed inset-x-0 top-0 z-50 flex justify-center px-3 pt-3 sm:px-4 sm:pt-4">
        <nav
          data-testid="floating-navbar"
          className={`flex max-w-full items-center gap-1 rounded-full px-2.5 py-2 transition-all duration-300 ${
            scrolled
              ? "bg-cream/90 shadow-[0_10px_40px_rgba(0,0,0,0.12)] ring-1 ring-ink/5 backdrop-blur-xl"
              : "bg-cream/75 shadow-[0_6px_24px_rgba(0,0,0,0.06)] ring-1 ring-ink/5 backdrop-blur-lg"
          }`}
        >
          <Logo />

          {/* Enlaces (ícono + texto) — solo escritorio grande */}
          <div className="hidden items-center gap-0.5 xl:flex">
            <span className="mx-1 h-6 w-px bg-ink/10" aria-hidden="true" />
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 rounded-full px-3 py-2 text-[13px] font-medium transition-colors ${
                      isActive
                        ? "bg-ink text-cream"
                        : "text-ink/70 hover:bg-mist hover:text-ink"
                    }`
                  }
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2.2} />
                  <span className="whitespace-nowrap">{item.label}</span>
                </NavLink>
              );
            })}

            {/* Comercial (dropdown con Patrocinadores + Marcas) */}
            <div className="group relative" data-testid="nav-comercial-group">
              <button
                type="button"
                data-testid="nav-comercial"
                className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-[13px] font-medium transition-colors ${
                  b2bActive ? "bg-ink text-cream" : "text-ink/70 hover:bg-mist hover:text-ink"
                }`}
                aria-haspopup="true"
              >
                <Briefcase className="h-3.5 w-3.5" strokeWidth={2.2} />
                <span className="whitespace-nowrap">{t("nav.comercial")}</span>
                <ChevronDown className="h-3 w-3 opacity-60" />
              </button>
              <div className="invisible absolute right-0 top-full z-50 w-56 translate-y-1 pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
                <div className="overflow-hidden rounded-2xl bg-cream p-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.15)] ring-1 ring-ink/10">
                  {NAV_B2B.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      data-testid={`nav-comercial-${item.to.replace("/", "")}`}
                      className={({ isActive }) =>
                        `block rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                          isActive ? "bg-ink text-cream" : "text-ink/75 hover:bg-mist"
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Acciones */}
          <span className="mx-1 hidden h-6 w-px bg-ink/10 xl:block" aria-hidden="true" />
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="hidden h-9 items-center rounded-full px-3 font-mono text-xs font-semibold uppercase tracking-widest text-ink/70 ring-1 ring-ink/15 transition-colors hover:text-coral hover:ring-coral xl:flex"
          >
            {t("lang.switch")}
          </button>
          <Link
            to="/vip"
            data-testid="nav-vip-cta"
            className="hidden items-center gap-1.5 rounded-full bg-coral px-4 py-2.5 text-[13px] font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#ff9d94] xl:flex"
          >
            <Crown className="h-3.5 w-3.5" strokeWidth={2.4} />
            {t("nav.vip")}
          </Link>

          {/* Móvil / tablet */}
          <div className="flex items-center gap-1.5 xl:hidden">
            <button
              onClick={toggle}
              data-testid="lang-toggle-mobile"
              className="flex h-9 items-center rounded-full px-3 font-mono text-xs font-semibold uppercase tracking-widest text-ink ring-1 ring-ink/15"
            >
              {t("lang.switch")}
            </button>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(!open)}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-ink text-cream"
              aria-label="Menú"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 overflow-y-auto bg-cream pt-24 xl:hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6">
              <div className="grid gap-1">
                {NAV.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={item.to}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04 }}
                    >
                      <NavLink
                        to={item.to}
                        className={({ isActive }) =>
                          `flex items-center gap-4 rounded-2xl px-5 py-4 font-display text-2xl font-bold tracking-tight ${
                            isActive ? "bg-ink text-cream" : "text-ink"
                          }`
                        }
                      >
                        <Icon className="h-5 w-5 shrink-0" />
                        {item.label}
                      </NavLink>
                    </motion.div>
                  );
                })}
                <NavLink
                  to="/vip"
                  className={({ isActive }) =>
                    `mt-1 flex items-center gap-4 rounded-2xl px-5 py-4 font-display text-2xl font-bold tracking-tight ${
                      isActive ? "bg-coral text-white" : "bg-coral/15 text-coral"
                    }`
                  }
                >
                  <Crown className="h-5 w-5 shrink-0" />
                  {t("nav.vip")}
                </NavLink>
              </div>
              <div className="mt-6 border-t border-ink/10 pt-6">
                <p className="mb-3 font-mono text-xs uppercase tracking-widest text-ink/40">
                  {t("nav.comercial")}
                </p>
                <div className="grid gap-1">
                  {NAV_B2B.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className="block rounded-2xl px-5 py-3 text-lg font-semibold text-ink/80"
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
              <div className="mt-6 space-y-4 border-t border-ink/10 pt-6">
                <NetworkLogo theme="light" compact />
                <MainSponsor variant="inline" theme="light" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
